import { WEB_IMAGE_CONFIG } from "../../Pages/DynamicEventTypes/constants";

const buildSearchUrl = (query, mimeType) => {
    const params = new URLSearchParams();
    params.set("q", query);
    params.set("page_size", WEB_IMAGE_CONFIG.pageSize);
    params.set("image_type", WEB_IMAGE_CONFIG.imageType);
    if (WEB_IMAGE_CONFIG.license) {
        params.set("license", WEB_IMAGE_CONFIG.license);
    }
    if (mimeType) {
        params.set("mime_type", mimeType);
    }
    return `${WEB_IMAGE_CONFIG.apiBaseUrl}?${params.toString()}`;
};

const getSearchQueries = (displayName, prompt) => {
    const primary = (displayName || "").trim();
    const fallback = (prompt || "").trim();
    const queries = [];
    if (primary) {
        queries.push(primary);
        if (WEB_IMAGE_CONFIG.searchFallbackSuffix) {
            queries.push(`${primary} ${WEB_IMAGE_CONFIG.searchFallbackSuffix}`);
        }
    }
    if (fallback) {
        queries.push(fallback);
        if (WEB_IMAGE_CONFIG.searchFallbackSuffix) {
            queries.push(`${fallback} ${WEB_IMAGE_CONFIG.searchFallbackSuffix}`);
        }
    }
    return Array.from(new Set(queries));
};

const fetchSearchResults = async (query) => {
    const mimeTypes = WEB_IMAGE_CONFIG.allowedMimeTypes || [];
    const attempts = mimeTypes.length > 0 ? mimeTypes : [null];

    for (let index = 0; index < attempts.length; index += 1) {
        const mimeType = attempts[index];
        const url = buildSearchUrl(query, mimeType);
        const response = await fetch(url);
        if (!response.ok) {
            continue;
        }
        const data = await response.json();
        if (Array.isArray(data?.results) && data.results.length > 0) {
            return data.results;
        }
    }
    return [];
};

const getImageCandidates = (results) => {
    if (!Array.isArray(results)) {
        return [];
    }
    return results
        .map((result) => ({
            url: result?.url || "",
            thumbnail: result?.thumbnail || "",
            title: result?.title || ""
        }))
        .filter((result) => result.url || result.thumbnail)
        .slice(0, WEB_IMAGE_CONFIG.maxResultsToTry);
};

const isAllowedMimeType = (mimeType) => {
    if (!mimeType) {
        return false;
    }
    return (WEB_IMAGE_CONFIG.allowedMimeTypes || []).includes(mimeType);
};

const getExtensionForMimeType = (mimeType) => {
    if (mimeType === "image/jpeg") {
        return "jpg";
    }
    if (mimeType === "image/png") {
        return "png";
    }
    return "";
};

const fetchImageFile = async (url, baseName) => {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) {
        throw new Error("image_fetch_failed");
    }
    const blob = await response.blob();
    const mimeType = blob.type;
    if (!isAllowedMimeType(mimeType)) {
        throw new Error("image_type_not_supported");
    }
    const extension = getExtensionForMimeType(mimeType);
    if (!extension) {
        throw new Error("image_type_not_supported");
    }
    const fileName = `${baseName}.${extension}`;
    return new File([blob], fileName, { type: mimeType });
};

const buildBaseFileName = (displayName) => {
    const base = (displayName || WEB_IMAGE_CONFIG.fileBaseName || "placeholder")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
    return base || "placeholder";
};

export const fetchWebImageForEventType = async ({ displayName, prompt }) => {
    const queries = getSearchQueries(displayName, prompt);
    const baseFileName = buildBaseFileName(displayName);

    for (let queryIndex = 0; queryIndex < queries.length; queryIndex += 1) {
        const query = queries[queryIndex];
        const results = await fetchSearchResults(query);
        const candidates = getImageCandidates(results);
        for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
            const candidate = candidates[candidateIndex];
            const urlCandidates = [candidate.url, candidate.thumbnail].filter(Boolean);
            for (let urlIndex = 0; urlIndex < urlCandidates.length; urlIndex += 1) {
                try {
                    const file = await fetchImageFile(urlCandidates[urlIndex], baseFileName);
                    const previewUrl = URL.createObjectURL(file);
                    return {
                        file,
                        previewUrl,
                        sourceUrl: candidate.url || urlCandidates[urlIndex],
                        isObjectUrl: true
                    };
                } catch (error) {
                    continue;
                }
            }
        }
    }
    throw new Error("web_image_not_found");
};
